/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AddGroupMembersPathParameters,
  AssignRolesToGroupPathParameters,
  DeleteGroupPathParameters,
  GetGroupMembersPathParameters,
  GetGroupPathParameters,
  GetGroupRolesPathParameters,
  GetUserGroupsPathParameters,
  ListAuthzDirectoryGroupsParams,
  ListGroupsParams,
  RemoveGroupMembersPathParameters,
  RemoveRolesFromGroupPathParameters,
  TLGroupCreateRequest,
  TLGroupMemberAddRequest,
  TLGroupMemberOperationResult,
  TLGroupMemberRemoveRequest,
  TLGroupMembersResponse,
  TLGroupRoleAssignRequest,
  TLGroupRoleRemoveRequest,
  TLGroupRolesResponse,
  TLGroupsGroupResponse,
  TLGroupsListResponse,
  TLGroupUpdateRequest,
  TLHTTPValidationError,
  TLPaginatedResponseAuthzGroupDirectoryEntry,
  UpdateGroupPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * List all groups with optional search filter.
 * @summary List Groups
 */
export const listGroups = (
  params?: ListGroupsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupsListResponse>({ url: `/api/v1/authz/groups`, method: 'GET', params, signal }, options);
};

export const getListGroupsQueryKey = (params?: ListGroupsParams) => {
  return ['api', 'v1', 'authz', 'groups', ...(params ? [params] : [])] as const;
};

export const getListGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof listGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListGroupsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listGroups>>> = ({ signal }) =>
    listGroups(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listGroups>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listGroups>>>;
export type ListGroupsQueryError = ErrorType<TLHTTPValidationError>;

export function useListGroups<
  TData = Awaited<ReturnType<typeof listGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListGroupsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listGroups>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGroups>>,
          TError,
          Awaited<ReturnType<typeof listGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListGroups<
  TData = Awaited<ReturnType<typeof listGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listGroups>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGroups>>,
          TError,
          Awaited<ReturnType<typeof listGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListGroups<
  TData = Awaited<ReturnType<typeof listGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Groups
 */

export function useListGroups<
  TData = Awaited<ReturnType<typeof listGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListGroupsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Groups
 */
export const prefetchListGroupsQuery = async <
  TData = Awaited<ReturnType<typeof listGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListGroupsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Groups
 */
export const invalidateListGroups = async (
  queryClient: QueryClient,
  params?: ListGroupsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListGroupsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Create a top-level group.
 * @summary Create Group
 */
export const createGroup = (
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupsGroupResponse>(
    {
      url: `/api/v1/authz/groups`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGroupCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateGroupQueryKey = (tLGroupCreateRequest?: BodyType<TLGroupCreateRequest>) => {
  return ['POST', 'api', 'v1', 'authz', 'groups', tLGroupCreateRequest] as const;
};

export const getCreateGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof createGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateGroupQueryKey(tLGroupCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createGroup>>> = ({ signal }) =>
    createGroup(tLGroupCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateGroupQueryResult = NonNullable<Awaited<ReturnType<typeof createGroup>>>;
export type CreateGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateGroup<
  TData = Awaited<ReturnType<typeof createGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createGroup>>,
          TError,
          Awaited<ReturnType<typeof createGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateGroup<
  TData = Awaited<ReturnType<typeof createGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createGroup>>,
          TError,
          Awaited<ReturnType<typeof createGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateGroup<
  TData = Awaited<ReturnType<typeof createGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Group
 */

export function useCreateGroup<
  TData = Awaited<ReturnType<typeof createGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateGroupQueryOptions(tLGroupCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Group
 */
export const prefetchCreateGroupQuery = async <
  TData = Awaited<ReturnType<typeof createGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateGroupQueryOptions(tLGroupCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Group
 */
export const invalidateCreateGroup = async (
  queryClient: QueryClient,
  tLGroupCreateRequest: BodyType<TLGroupCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateGroupQueryKey(tLGroupCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary List Authz Directory Groups
 */
export const listAuthzDirectoryGroups = (
  params?: ListAuthzDirectoryGroupsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseAuthzGroupDirectoryEntry>(
    { url: `/api/v1/authz/groups/directory`, method: 'GET', params, signal },
    options,
  );
};

export const getListAuthzDirectoryGroupsQueryKey = (params?: ListAuthzDirectoryGroupsParams) => {
  return ['api', 'v1', 'authz', 'groups', 'directory', ...(params ? [params] : [])] as const;
};

export const getListAuthzDirectoryGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAuthzDirectoryGroupsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>> = ({ signal }) =>
    listAuthzDirectoryGroups(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAuthzDirectoryGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>>;
export type ListAuthzDirectoryGroupsQueryError = ErrorType<TLHTTPValidationError>;

export function useListAuthzDirectoryGroups<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListAuthzDirectoryGroupsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
          TError,
          Awaited<ReturnType<typeof listAuthzDirectoryGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAuthzDirectoryGroups<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
          TError,
          Awaited<ReturnType<typeof listAuthzDirectoryGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAuthzDirectoryGroups<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Authz Directory Groups
 */

export function useListAuthzDirectoryGroups<
  TData = Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListAuthzDirectoryGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAuthzDirectoryGroupsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Authz Directory Groups
 */
export const prefetchListAuthzDirectoryGroupsQuery = async <
  TData = Awaited<ReturnType<typeof listAuthzDirectoryGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListAuthzDirectoryGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuthzDirectoryGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListAuthzDirectoryGroupsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Authz Directory Groups
 */
export const invalidateListAuthzDirectoryGroups = async (
  queryClient: QueryClient,
  params?: ListAuthzDirectoryGroupsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListAuthzDirectoryGroupsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Get all groups that a user is a member of.
 * @summary Get User Groups
 */
export const getUserGroups = (
  { email }: GetUserGroupsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupsListResponse>(
    { url: `/api/v1/authz/groups/users/${email}/groups`, method: 'GET', signal },
    options,
  );
};

export const getGetUserGroupsQueryKey = ({ email }: GetUserGroupsPathParameters) => {
  return ['api', 'v1', 'authz', 'groups', 'users', email, 'groups'] as const;
};

export const getGetUserGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: GetUserGroupsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserGroupsQueryKey({ email });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserGroups>>> = ({ signal }) =>
    getUserGroups({ email }, requestOptions, signal);

  return { queryKey, queryFn, enabled: email !== null && email !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserGroups>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof getUserGroups>>>;
export type GetUserGroupsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUserGroups<
  TData = Awaited<ReturnType<typeof getUserGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUserGroupsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserGroups>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserGroups>>,
          TError,
          Awaited<ReturnType<typeof getUserGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserGroups<
  TData = Awaited<ReturnType<typeof getUserGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUserGroupsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserGroups>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserGroups>>,
          TError,
          Awaited<ReturnType<typeof getUserGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserGroups<
  TData = Awaited<ReturnType<typeof getUserGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetUserGroupsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Groups
 */

export function useGetUserGroups<
  TData = Awaited<ReturnType<typeof getUserGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { email }: GetUserGroupsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserGroupsQueryOptions({ email }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get User Groups
 */
export const prefetchGetUserGroupsQuery = async <
  TData = Awaited<ReturnType<typeof getUserGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { email }: GetUserGroupsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUserGroupsQueryOptions({ email }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get User Groups
 */
export const invalidateGetUserGroups = async (
  queryClient: QueryClient,
  { email }: GetUserGroupsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUserGroupsQueryKey({ email }) }, options);

  return queryClient;
};

/**
 * Delete a group.
 * @summary Delete Group
 */
export const deleteGroup = (
  { groupId }: DeleteGroupPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/api/v1/authz/groups/${groupId}`, method: 'DELETE', signal }, options);
};

export const getDeleteGroupQueryKey = ({ groupId }: DeleteGroupPathParameters) => {
  return ['DELETE', 'api', 'v1', 'authz', 'groups', groupId] as const;
};

export const getDeleteGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: DeleteGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteGroupQueryKey({ groupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteGroup>>> = ({ signal }) =>
    deleteGroup({ groupId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteGroupQueryResult = NonNullable<Awaited<ReturnType<typeof deleteGroup>>>;
export type DeleteGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteGroup<
  TData = Awaited<ReturnType<typeof deleteGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteGroupPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteGroup>>,
          TError,
          Awaited<ReturnType<typeof deleteGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteGroup<
  TData = Awaited<ReturnType<typeof deleteGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteGroup>>,
          TError,
          Awaited<ReturnType<typeof deleteGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteGroup<
  TData = Awaited<ReturnType<typeof deleteGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Group
 */

export function useDeleteGroup<
  TData = Awaited<ReturnType<typeof deleteGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: DeleteGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteGroupQueryOptions({ groupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Group
 */
export const prefetchDeleteGroupQuery = async <
  TData = Awaited<ReturnType<typeof deleteGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: DeleteGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteGroupQueryOptions({ groupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Group
 */
export const invalidateDeleteGroup = async (
  queryClient: QueryClient,
  { groupId }: DeleteGroupPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteGroupQueryKey({ groupId }) }, options);

  return queryClient;
};

/**
 * Get a group by ID.
 * @summary Get Group
 */
export const getGroup = (
  { groupId }: GetGroupPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupsGroupResponse>({ url: `/api/v1/authz/groups/${groupId}`, method: 'GET', signal }, options);
};

export const getGetGroupQueryKey = ({ groupId }: GetGroupPathParameters) => {
  return ['api', 'v1', 'authz', 'groups', groupId] as const;
};

export const getGetGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof getGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: GetGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGroupQueryKey({ groupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGroup>>> = ({ signal }) =>
    getGroup({ groupId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupQueryResult = NonNullable<Awaited<ReturnType<typeof getGroup>>>;
export type GetGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useGetGroup<TData = Awaited<ReturnType<typeof getGroup>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetGroupPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getGroup>>, TError, Awaited<ReturnType<typeof getGroup>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroup<TData = Awaited<ReturnType<typeof getGroup>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof getGroup>>, TError, Awaited<ReturnType<typeof getGroup>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroup<TData = Awaited<ReturnType<typeof getGroup>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Group
 */

export function useGetGroup<TData = Awaited<ReturnType<typeof getGroup>>, TError = ErrorType<TLHTTPValidationError>>(
  { groupId }: GetGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetGroupQueryOptions({ groupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Group
 */
export const prefetchGetGroupQuery = async <
  TData = Awaited<ReturnType<typeof getGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: GetGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetGroupQueryOptions({ groupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Group
 */
export const invalidateGetGroup = async (
  queryClient: QueryClient,
  { groupId }: GetGroupPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetGroupQueryKey({ groupId }) }, options);

  return queryClient;
};

/**
 * Update a group.
 * @summary Update Group
 */
export const updateGroup = (
  { groupId }: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupsGroupResponse>(
    {
      url: `/api/v1/authz/groups/${groupId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLGroupUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateGroupQueryKey = (
  { groupId }: UpdateGroupPathParameters,
  tLGroupUpdateRequest?: BodyType<TLGroupUpdateRequest>,
) => {
  return ['PUT', 'api', 'v1', 'authz', 'groups', groupId, tLGroupUpdateRequest] as const;
};

export const getUpdateGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof updateGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateGroupQueryKey({ groupId }, tLGroupUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateGroup>>> = ({ signal }) =>
    updateGroup({ groupId }, tLGroupUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateGroupQueryResult = NonNullable<Awaited<ReturnType<typeof updateGroup>>>;
export type UpdateGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateGroup<
  TData = Awaited<ReturnType<typeof updateGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateGroup>>,
          TError,
          Awaited<ReturnType<typeof updateGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateGroup<
  TData = Awaited<ReturnType<typeof updateGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateGroup>>,
          TError,
          Awaited<ReturnType<typeof updateGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateGroup<
  TData = Awaited<ReturnType<typeof updateGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Group
 */

export function useUpdateGroup<
  TData = Awaited<ReturnType<typeof updateGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateGroupQueryOptions({ groupId }, tLGroupUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Group
 */
export const prefetchUpdateGroupQuery = async <
  TData = Awaited<ReturnType<typeof updateGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateGroupQueryOptions({ groupId }, tLGroupUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Group
 */
export const invalidateUpdateGroup = async (
  queryClient: QueryClient,
  { groupId }: UpdateGroupPathParameters,
  tLGroupUpdateRequest: BodyType<TLGroupUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getUpdateGroupQueryKey({ groupId }, tLGroupUpdateRequest) }, options);

  return queryClient;
};

/**
 * Remove members from a group.
 * @summary Remove Group Members
 */
export const removeGroupMembers = (
  { groupId }: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupMemberOperationResult[]>(
    {
      url: `/api/v1/authz/groups/${groupId}/members`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLGroupMemberRemoveRequest,
      signal,
    },
    options,
  );
};

export const getRemoveGroupMembersQueryKey = (
  { groupId }: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest?: BodyType<TLGroupMemberRemoveRequest>,
) => {
  return ['DELETE', 'api', 'v1', 'authz', 'groups', groupId, 'members', tLGroupMemberRemoveRequest] as const;
};

export const getRemoveGroupMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof removeGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemoveGroupMembersQueryKey({ groupId }, tLGroupMemberRemoveRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeGroupMembers>>> = ({ signal }) =>
    removeGroupMembers({ groupId }, tLGroupMemberRemoveRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof removeGroupMembers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RemoveGroupMembersQueryResult = NonNullable<Awaited<ReturnType<typeof removeGroupMembers>>>;
export type RemoveGroupMembersQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveGroupMembers<
  TData = Awaited<ReturnType<typeof removeGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeGroupMembers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof removeGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveGroupMembers<
  TData = Awaited<ReturnType<typeof removeGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeGroupMembers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof removeGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveGroupMembers<
  TData = Awaited<ReturnType<typeof removeGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Group Members
 */

export function useRemoveGroupMembers<
  TData = Awaited<ReturnType<typeof removeGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveGroupMembersQueryOptions({ groupId }, tLGroupMemberRemoveRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Group Members
 */
export const prefetchRemoveGroupMembersQuery = async <
  TData = Awaited<ReturnType<typeof removeGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveGroupMembersQueryOptions({ groupId }, tLGroupMemberRemoveRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Group Members
 */
export const invalidateRemoveGroupMembers = async (
  queryClient: QueryClient,
  { groupId }: RemoveGroupMembersPathParameters,
  tLGroupMemberRemoveRequest: BodyType<TLGroupMemberRemoveRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemoveGroupMembersQueryKey({ groupId }, tLGroupMemberRemoveRequest) },
    options,
  );

  return queryClient;
};

/**
 * Get all members of a group.
 * @summary Get Group Members
 */
export const getGroupMembers = (
  { groupId }: GetGroupMembersPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupMembersResponse>(
    { url: `/api/v1/authz/groups/${groupId}/members`, method: 'GET', signal },
    options,
  );
};

export const getGetGroupMembersQueryKey = ({ groupId }: GetGroupMembersPathParameters) => {
  return ['api', 'v1', 'authz', 'groups', groupId, 'members'] as const;
};

export const getGetGroupMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof getGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: GetGroupMembersPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGroupMembersQueryKey({ groupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGroupMembers>>> = ({ signal }) =>
    getGroupMembers({ groupId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getGroupMembers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupMembersQueryResult = NonNullable<Awaited<ReturnType<typeof getGroupMembers>>>;
export type GetGroupMembersQueryError = ErrorType<TLHTTPValidationError>;

export function useGetGroupMembers<
  TData = Awaited<ReturnType<typeof getGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetGroupMembersPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupMembers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof getGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupMembers<
  TData = Awaited<ReturnType<typeof getGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetGroupMembersPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupMembers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof getGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupMembers<
  TData = Awaited<ReturnType<typeof getGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetGroupMembersPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Group Members
 */

export function useGetGroupMembers<
  TData = Awaited<ReturnType<typeof getGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: GetGroupMembersPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetGroupMembersQueryOptions({ groupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Group Members
 */
export const prefetchGetGroupMembersQuery = async <
  TData = Awaited<ReturnType<typeof getGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: GetGroupMembersPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetGroupMembersQueryOptions({ groupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Group Members
 */
export const invalidateGetGroupMembers = async (
  queryClient: QueryClient,
  { groupId }: GetGroupMembersPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetGroupMembersQueryKey({ groupId }) }, options);

  return queryClient;
};

/**
 * Add members to a group.
 * @summary Add Group Members
 */
export const addGroupMembers = (
  { groupId }: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupMemberOperationResult[]>(
    {
      url: `/api/v1/authz/groups/${groupId}/members`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGroupMemberAddRequest,
      signal,
    },
    options,
  );
};

export const getAddGroupMembersQueryKey = (
  { groupId }: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest?: BodyType<TLGroupMemberAddRequest>,
) => {
  return ['POST', 'api', 'v1', 'authz', 'groups', groupId, 'members', tLGroupMemberAddRequest] as const;
};

export const getAddGroupMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof addGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddGroupMembersQueryKey({ groupId }, tLGroupMemberAddRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addGroupMembers>>> = ({ signal }) =>
    addGroupMembers({ groupId }, tLGroupMemberAddRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof addGroupMembers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AddGroupMembersQueryResult = NonNullable<Awaited<ReturnType<typeof addGroupMembers>>>;
export type AddGroupMembersQueryError = ErrorType<TLHTTPValidationError>;

export function useAddGroupMembers<
  TData = Awaited<ReturnType<typeof addGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addGroupMembers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof addGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddGroupMembers<
  TData = Awaited<ReturnType<typeof addGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addGroupMembers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addGroupMembers>>,
          TError,
          Awaited<ReturnType<typeof addGroupMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddGroupMembers<
  TData = Awaited<ReturnType<typeof addGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Group Members
 */

export function useAddGroupMembers<
  TData = Awaited<ReturnType<typeof addGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddGroupMembersQueryOptions({ groupId }, tLGroupMemberAddRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Group Members
 */
export const prefetchAddGroupMembersQuery = async <
  TData = Awaited<ReturnType<typeof addGroupMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addGroupMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddGroupMembersQueryOptions({ groupId }, tLGroupMemberAddRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Group Members
 */
export const invalidateAddGroupMembers = async (
  queryClient: QueryClient,
  { groupId }: AddGroupMembersPathParameters,
  tLGroupMemberAddRequest: BodyType<TLGroupMemberAddRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddGroupMembersQueryKey({ groupId }, tLGroupMemberAddRequest) },
    options,
  );

  return queryClient;
};

/**
 * Get all roles assigned to a group.
 * @summary Get Group Roles
 */
export const getGroupRoles = (
  { groupId }: GetGroupRolesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupRolesResponse>(
    { url: `/api/v1/authz/groups/${groupId}/roles`, method: 'GET', signal },
    options,
  );
};

export const getGetGroupRolesQueryKey = ({ groupId }: GetGroupRolesPathParameters) => {
  return ['api', 'v1', 'authz', 'groups', groupId, 'roles'] as const;
};

export const getGetGroupRolesQueryOptions = <
  TData = Awaited<ReturnType<typeof getGroupRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: GetGroupRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGroupRolesQueryKey({ groupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGroupRoles>>> = ({ signal }) =>
    getGroupRoles({ groupId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getGroupRoles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupRolesQueryResult = NonNullable<Awaited<ReturnType<typeof getGroupRoles>>>;
export type GetGroupRolesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetGroupRoles<
  TData = Awaited<ReturnType<typeof getGroupRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetGroupRolesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupRoles>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGroupRoles>>,
          TError,
          Awaited<ReturnType<typeof getGroupRoles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupRoles<
  TData = Awaited<ReturnType<typeof getGroupRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetGroupRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupRoles>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGroupRoles>>,
          TError,
          Awaited<ReturnType<typeof getGroupRoles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupRoles<
  TData = Awaited<ReturnType<typeof getGroupRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetGroupRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Group Roles
 */

export function useGetGroupRoles<
  TData = Awaited<ReturnType<typeof getGroupRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: GetGroupRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetGroupRolesQueryOptions({ groupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Group Roles
 */
export const prefetchGetGroupRolesQuery = async <
  TData = Awaited<ReturnType<typeof getGroupRoles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: GetGroupRolesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getGroupRoles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetGroupRolesQueryOptions({ groupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Group Roles
 */
export const invalidateGetGroupRoles = async (
  queryClient: QueryClient,
  { groupId }: GetGroupRolesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetGroupRolesQueryKey({ groupId }) }, options);

  return queryClient;
};

/**
 * Assign roles to a group.
 * @summary Assign Roles To Group
 */
export const assignRolesToGroup = (
  { groupId }: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGroupRolesResponse>(
    {
      url: `/api/v1/authz/groups/${groupId}/roles/assign`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGroupRoleAssignRequest,
      signal,
    },
    options,
  );
};

export const getAssignRolesToGroupQueryKey = (
  { groupId }: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest?: BodyType<TLGroupRoleAssignRequest>,
) => {
  return ['POST', 'api', 'v1', 'authz', 'groups', groupId, 'roles', 'assign', tLGroupRoleAssignRequest] as const;
};

export const getAssignRolesToGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof assignRolesToGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignRolesToGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAssignRolesToGroupQueryKey({ groupId }, tLGroupRoleAssignRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof assignRolesToGroup>>> = ({ signal }) =>
    assignRolesToGroup({ groupId }, tLGroupRoleAssignRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof assignRolesToGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AssignRolesToGroupQueryResult = NonNullable<Awaited<ReturnType<typeof assignRolesToGroup>>>;
export type AssignRolesToGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useAssignRolesToGroup<
  TData = Awaited<ReturnType<typeof assignRolesToGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignRolesToGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignRolesToGroup>>,
          TError,
          Awaited<ReturnType<typeof assignRolesToGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignRolesToGroup<
  TData = Awaited<ReturnType<typeof assignRolesToGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignRolesToGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignRolesToGroup>>,
          TError,
          Awaited<ReturnType<typeof assignRolesToGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignRolesToGroup<
  TData = Awaited<ReturnType<typeof assignRolesToGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignRolesToGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Assign Roles To Group
 */

export function useAssignRolesToGroup<
  TData = Awaited<ReturnType<typeof assignRolesToGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignRolesToGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAssignRolesToGroupQueryOptions({ groupId }, tLGroupRoleAssignRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Assign Roles To Group
 */
export const prefetchAssignRolesToGroupQuery = async <
  TData = Awaited<ReturnType<typeof assignRolesToGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignRolesToGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAssignRolesToGroupQueryOptions({ groupId }, tLGroupRoleAssignRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Assign Roles To Group
 */
export const invalidateAssignRolesToGroup = async (
  queryClient: QueryClient,
  { groupId }: AssignRolesToGroupPathParameters,
  tLGroupRoleAssignRequest: BodyType<TLGroupRoleAssignRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAssignRolesToGroupQueryKey({ groupId }, tLGroupRoleAssignRequest) },
    options,
  );

  return queryClient;
};

/**
 * Remove roles from a group.
 * @summary Remove Roles From Group
 */
export const removeRolesFromGroup = (
  { groupId }: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>(
    {
      url: `/api/v1/authz/groups/${groupId}/roles/remove`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      data: tLGroupRoleRemoveRequest,
      signal,
    },
    options,
  );
};

export const getRemoveRolesFromGroupQueryKey = (
  { groupId }: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest?: BodyType<TLGroupRoleRemoveRequest>,
) => {
  return ['DELETE', 'api', 'v1', 'authz', 'groups', groupId, 'roles', 'remove', tLGroupRoleRemoveRequest] as const;
};

export const getRemoveRolesFromGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof removeRolesFromGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeRolesFromGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemoveRolesFromGroupQueryKey({ groupId }, tLGroupRoleRemoveRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeRolesFromGroup>>> = ({ signal }) =>
    removeRolesFromGroup({ groupId }, tLGroupRoleRemoveRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: groupId !== null && groupId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof removeRolesFromGroup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RemoveRolesFromGroupQueryResult = NonNullable<Awaited<ReturnType<typeof removeRolesFromGroup>>>;
export type RemoveRolesFromGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveRolesFromGroup<
  TData = Awaited<ReturnType<typeof removeRolesFromGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeRolesFromGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeRolesFromGroup>>,
          TError,
          Awaited<ReturnType<typeof removeRolesFromGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveRolesFromGroup<
  TData = Awaited<ReturnType<typeof removeRolesFromGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeRolesFromGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeRolesFromGroup>>,
          TError,
          Awaited<ReturnType<typeof removeRolesFromGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveRolesFromGroup<
  TData = Awaited<ReturnType<typeof removeRolesFromGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeRolesFromGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Roles From Group
 */

export function useRemoveRolesFromGroup<
  TData = Awaited<ReturnType<typeof removeRolesFromGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { groupId }: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeRolesFromGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveRolesFromGroupQueryOptions({ groupId }, tLGroupRoleRemoveRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Roles From Group
 */
export const prefetchRemoveRolesFromGroupQuery = async <
  TData = Awaited<ReturnType<typeof removeRolesFromGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { groupId }: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeRolesFromGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveRolesFromGroupQueryOptions({ groupId }, tLGroupRoleRemoveRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Roles From Group
 */
export const invalidateRemoveRolesFromGroup = async (
  queryClient: QueryClient,
  { groupId }: RemoveRolesFromGroupPathParameters,
  tLGroupRoleRemoveRequest: BodyType<TLGroupRoleRemoveRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRemoveRolesFromGroupQueryKey({ groupId }, tLGroupRoleRemoveRequest) },
    options,
  );

  return queryClient;
};
